import { Helmet } from 'react-helmet-async';
import { Box, Button, Grid, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Alert, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { PATH_DASHBOARD } from '../../routes/paths';
import { Link as RouterLink } from 'react-router-dom';
import { getFirmwareList, deleteFirmware, triggerOTA } from '../../api';
import ConfirmDialog from 'src/components/confirm-dialog';
import { useSnackbar } from 'src/components/snackbar';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFTextField } from '../../components/hook-form';
import dayjs from 'dayjs';
import TableNoData from 'src/components/table/TableNoData';
import Iconify from '../../components/iconify';
import { fData } from '../../utils/formatNumber';

const DEFAULT_ROWS_PER_PAGE = 10;

export default function OTAManageList() {
  const [page, setPage] = React.useState(0);
  const [rowsLength, setRowsLength] = useState(0);
  const [rows, setRows] = useState<any>([]);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [t, setT] = useState(1);
  const [delItem, setDel] = useState<any>();
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openTriggerDialog, setOpenTriggerDialog] = useState(false);
  const [triggerItem, setTriggerItem] = useState<any>();
  const [triggerData, setTriggerData] = useState({ device_id: '' });
  const { enqueueSnackbar } = useSnackbar();

  const defaultValues = {
    hardware_version: '',
  };

  const methods = useForm({
    defaultValues,
  });

  const {
    reset,
    watch,
    handleSubmit,
  } = methods;

  const values = watch();

  const handleSearchFirmware = React.useCallback(async () => {
    var arr1 = JSON.parse(JSON.stringify(values));
    for (var key in arr1) {
      if (arr1[key] === '') {
        delete arr1[key];
      }
    }
    let res: any = await getFirmwareList({
      page: page + 1,
      page_size: rowsPerPage,
      ...arr1,
    });
    console.log(res);
    if (res.code === 200) {
      var arr = [...res.data.items || []];
      setRowsLength(res.data.total);
      setRows(arr);
    }
  }, [page, rowsPerPage, values]);

  useEffect(() => {
    handleSearchFirmware();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, t]);

  const handleChangePage = React.useCallback(
    (event: unknown, newPage: number) => {
      setPage(newPage);
    },
    [],
  );

  const handleChangeRowsPerPage = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const updatedRowsPerPage = parseInt(event.target.value, 10);
      setRowsPerPage(updatedRowsPerPage);
      setPage(0);
    },
    [],
  );

  const handleDelete = async () => {
    var res: any = await deleteFirmware(delItem.id);
    if (res.code === 200) {
      enqueueSnackbar('删除成功！');
      setOpenConfirm(false);
      setT(t + 1);
    } else {
      enqueueSnackbar(res.message || '删除失败！', { variant: 'error' });
    }
  };

  const handleTriggerOTA = async () => {
    // 验证输入框是否有值
    if (!triggerData.device_id || triggerData.device_id.trim() === '') {
      enqueueSnackbar('请输入设备序列号！', { variant: 'error' });
      return;
    }

    try {
      const data: any = {
        sn: triggerData.device_id.trim()
      };

      const res: any = await triggerOTA(triggerItem.id, data);
      if (res.code === 200 || res.code === 202) {
        enqueueSnackbar('OTA升级已触发！');
        setOpenTriggerDialog(false);
        setTriggerData({ device_id: '' });
        setT(t + 1);
      } else {
        enqueueSnackbar(res.message || '触发失败！', { variant: 'error' });
      }
    } catch (error) {
      console.error('Trigger OTA error:', error);
      enqueueSnackbar('触发失败！', { variant: 'error' });
    }
  };

  const onSubmit = async () => {
    setPage(0);
    setT(t + 1);
  };

  return (
    <>
      <Helmet>
        <title>OTA固件管理</title>
      </Helmet>

      <Typography variant="h4" component="h2">
        OTA固件管理
      </Typography>

      <Box sx={{ width: '100%' }}>
        <Paper sx={{ width: '100%', mb: 2, mt: 6 }}>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={9}>
                <Box
                  sx={{
                    '& > :not(style)': { m: 1, width: '25ch' },
                  }}
                >
                  <RHFTextField name="hardware_version" label="硬件版本" />
                </Box>
              </Grid>
              <Grid item xs={2}>
                <Button variant="contained" sx={{ m: '10px' }} type="submit">
                  搜索
                </Button>
                <Button variant="outlined" sx={{ m: '10px' }} onClick={() => { setPage(0); reset(); setT(t + 1); }}>
                  重置
                </Button>
              </Grid>
            </Grid>
          </FormProvider>
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            spacing={2}
            mr={3}
          >
            <Button variant="contained" component={RouterLink} sx={{ m: '10px' }} to={PATH_DASHBOARD.otaManage.upload}>
              上传固件
            </Button>
          </Stack>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 1200 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>版本号</TableCell>
                  <TableCell align="center">硬件版本</TableCell>
                  <TableCell align="center">文件名</TableCell>
                  <TableCell align="center">文件大小</TableCell>
                  <TableCell align="center">校验和</TableCell>
                  <TableCell align="center">发布说明</TableCell>
                  <TableCell align="center">创建时间</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row: any) => (
                  <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row">
                      {row.version}
                    </TableCell>
                    <TableCell align="center">{row.hardware_version}</TableCell>
                    <TableCell align="center">{row.file_title}</TableCell>
                    <TableCell align="center">{fData(row.file_size)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={row.checksum}>
                        <Typography variant="body2" sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.checksum?.substring(0, 8)}...
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {row.release_notes || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{dayjs(row.created_at).format('YYYY-MM-DD HH:mm')}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="查看详情">
                          <IconButton
                            component={RouterLink}
                            to={`${PATH_DASHBOARD.otaManage.detail}?id=${row.id}`}
                            size="small"
                          >
                            <Iconify icon="eva:eye-fill" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="触发OTA升级">
                          <IconButton
                            onClick={() => { setOpenTriggerDialog(true); setTriggerItem(row); }}
                            size="small"
                            color="primary"
                          >
                            <Iconify icon="eva:upload-fill" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="删除">
                          <IconButton
                            onClick={() => { setOpenConfirm(true); setDel(row); }}
                            size="small"
                            color="error"
                          >
                            <Iconify icon="eva:trash-2-fill" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                <TableNoData isNotFound={rows.length === 0} />
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={rowsLength}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </Paper>
      </Box>

      <ConfirmDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        title="删除固件"
        content="确定删除此固件吗？此操作不可恢复！"
        action={
          <Button variant="contained" color="error" onClick={handleDelete}>
            确定
          </Button>
        }
      />

      {/* 触发OTA升级对话框 */}
      <Dialog open={openTriggerDialog} onClose={() => setOpenTriggerDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>触发OTA升级</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            请输入要升级的设备序列号
          </Typography>
          <Stack spacing={3}>
            <TextField
              label="设备序列号"
              value={triggerData.device_id}
              onChange={(e) => setTriggerData({ device_id: e.target.value })}
              placeholder="例如: YZNVT01252010001"
              helperText="请输入单个设备序列号"
            />
            <Alert severity="warning">
              <Typography variant="body2">
                请至少输入一个设备序列号
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTriggerDialog(false)}>取消</Button>
          <Button
            variant="contained"
            onClick={handleTriggerOTA}
          >
            触发升级
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
